import React, { useMemo, useEffect, useRef } from 'react';
import { Box, VStack, Text, HStack, Icon, Spinner, Center, Heading } from '@chakra-ui/react';
import { FiCalendar, FiX } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetMailsInfiniteQuery } from '../../api';
import MailCard from './MailCard';
import Button from '../base/Button';
import { formatDateRange, GetDate } from '../../utils/dateUtils';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Fragment } from 'react/jsx-runtime';
import { useGroupedMails } from '../../hooks/useGroupedMails';
import { useDateFilter } from '../../hooks/useDateFilter';

const MailList: React.FC = () => {
    const { t, locale } = useTranslation();

    const {
        data: mailsData,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetMailsInfiniteQuery(undefined, { refetchOnMountOrArgChange: true });

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Получаем все письма из всех страниц
    const allMails = useMemo(() => {
        return mailsData?.pages.flatMap((page) => page.mails) || [];
    }, [mailsData]);

    // Фильтруем письма по дате
    const {
        filteredItems: filteredMails,
        dateFilter,
        handleSearchByDate,
        handleClearFilter,
    } = useDateFilter({
        items: allMails,
        getDateFromItem: (mail) => mail.created_at,
    });

    useEffect(() => {
        if (filteredMails.length === 0 && hasNextPage) {
            fetchNextPage();
        }
    }, [filteredMails, hasNextPage]);

    // Определяем, есть ли еще данные для загрузки с учетом фильтра по дате
    const effectiveHasNextPage = useMemo(() => {
        if (!hasNextPage) {
            return false;
        }

        // Если есть фильтр "от" и самое новое письмо в кэше раньше этой даты,
        // то загружать дальше нет смысла
        if (dateFilter?.from && allMails.length > 0) {
            const lastMail = allMails[allMails.length - 1]; // Письма отсортированы от новых к старым
            const lastMailDate = GetDate(lastMail.created_at);
            const filterFromDate = GetDate(dateFilter.from);

            if (lastMailDate < filterFromDate) {
                return false;
            }
        }

        return hasNextPage;
    }, [hasNextPage, dateFilter, allMails]);

    // Группируем письма по периодам
    const groupedMails = useGroupedMails({ mails: filteredMails });

    // Проверяем, нужно ли загрузить следующую страницу при монтировании или изменении данных
    useEffect(() => {
        if (!scrollContainerRef.current || isLoading || isFetchingNextPage || !hasNextPage) {
            return;
        }

        const container = scrollContainerRef.current;
        // Если контента недостаточно для скролла, загружаем следующую страницу
        if (container.scrollHeight <= container.clientHeight) {
            fetchNextPage();
        }
    }, [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]);

    if (isLoading) {
        return (
            <Center py={8}>
                <Spinner size="lg" color="yellow.600" />
            </Center>
        );
    }

    return (
        <VStack
            gap={4}
            align="stretch"
            flex={1}
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
        >
            {/* Кнопка поиска по дате и выбранный диапазон */}
            <HStack gap={3} align="center" justify="space-between" flexWrap="wrap" flexShrink={0}>
                <Button buttonType="success" size="lg" onClick={handleSearchByDate}>
                    <HStack gap={3}>
                        <Icon as={FiCalendar} />
                        <Text>{t.pages.mail.searchByDate}</Text>
                    </HStack>
                </Button>
                {dateFilter && (dateFilter.from || dateFilter.to) && (
                    <HStack gap={2} align="center">
                        <Text fontSize="md" color="gray.500">
                            {formatDateRange(dateFilter.from, dateFilter.to, locale, {
                                dateFrom: t.pages.mail.dateFrom,
                                dateTo: t.pages.mail.dateTo,
                            })}
                        </Text>
                        <Box
                            as="button"
                            onClick={handleClearFilter}
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="gray.500"
                            _hover={{ color: 'gray.700' }}
                            aria-label={t.pages.mail.clearFilter}
                        >
                            <Icon as={FiX} boxSize={4} />
                        </Box>
                    </HStack>
                )}
            </HStack>

            {error && (
                <Text color="red.600" textAlign="center" py={4}>
                    {t.pages.mail.loadError}
                </Text>
            )}

            {filteredMails.length === 0 && !isLoading && !isFetchingNextPage && !error && (
                <Text color="gray.600" textAlign="center" py={4}>
                    {t.pages.mail.noMails}
                </Text>
            )}

            {/* Список с бесконечной прокруткой */}
            <Box
                id="scrollable-mail-list"
                ref={scrollContainerRef}
                flex={1}
                overflowY="auto"
                minH={0}
                style={{ WebkitOverflowScrolling: 'touch' }}
            >
                <InfiniteScroll
                    dataLength={mailsData?.pages.reduce((acc, page) => acc + page.mails.length, 0) || 0}
                    next={fetchNextPage}
                    hasMore={effectiveHasNextPage}
                    loader={
                        <Center py={4}>
                            <Spinner size="md" color="yellow.600" />
                        </Center>
                    }
                    scrollableTarget="scrollable-mail-list"
                    scrollThreshold={0.8}
                >
                    <VStack gap={0} align="stretch">
                        {groupedMails.map((group) => (
                            <Fragment key={group.period}>
                                {/* Заголовок периода */}
                                <Box mb={3} mt={groupedMails.indexOf(group) === 0 ? 0 : 4}>
                                    <Heading size="md" color="black" fontWeight="medium">
                                        {group.period}
                                    </Heading>
                                </Box>
                                {/* Письма группы */}
                                {group.mails.map((mail, index) => (
                                    <Fragment key={mail.id}>
                                        <MailCard mail={mail} />
                                        {/* Разделитель между письмами (кроме последнего) */}
                                        {index < group.mails.length - 1 && (
                                            <Box h="1px" bg="gray.400" w="full" />
                                        )}
                                    </Fragment>
                                ))}
                            </Fragment>
                        ))}
                    </VStack>
                </InfiniteScroll>
            </Box>
        </VStack>
    );
};

export default MailList;
